/* eslint-disable @typescript-eslint/no-unused-vars, no-unused-vars, no-await-in-loop, no-continue, react/no-array-index-key, no-nested-ternary */
import React, { useEffect, useState } from 'react';
import { useCreate, useRedirect, useNotify, useGetList } from 'react-admin';
import { logger, slugify } from '@musedrops/shared';
import { useSearchParams } from 'react-router-dom';
import {
  Button,
  CircularProgress,
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  Alert,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Radio,
  RadioGroup,
  FormLabel,
} from '@mui/material';
import { Copy, ArrowLeft } from 'lucide-react';
import { FormTextField, SearchSelect } from '../../../shared/components/forms';
import { useUnsavedChanges } from '../../../shared/hooks';
import { useStoryIdeas } from '../../../hooks/useStoryIdeas';
import { useQueueJob } from '../../../hooks/useQueueJob';
import { LAYOUT } from '../../../theme';
import { waitForJob } from '../../../services/queueJob';
import { getEdgeFunctionAuthHeaders } from '../../../services/supabase';

export const StoryCreateWrapper = () => {
  const [searchParams] = useSearchParams();
  const preselectedShowSlug = searchParams.get('show') || '';

  const [activeTab, setActiveTab] = useState(0);
  const [manualTopic, setManualTopic] = useState('');
  const [manualTitle, setManualTitle] = useState('');
  const [manualSlug, setManualSlug] = useState('');
  const [manualDescription, setManualDescription] = useState('');
  const [manualBrief, setManualBrief] = useState('');
  const [manualTakeaway, setManualTakeaway] = useState('');
  const [manualKeywords, setManualKeywords] = useState('');
  const [manualShowSlug, setManualShowSlug] = useState(preselectedShowSlug);
  const [generateShowSlug, setGenerateShowSlug] = useState(preselectedShowSlug);
  const [generateMode, setGenerateMode] = useState<'specific' | 'blast'>(
    'specific'
  );
  const [storyIdeaCount, setStoryIdeaCount] = useState(5);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedIdeas, setSelectedIdeas] = useState<boolean[]>([]);
  const [showDetails, setShowDetails] = useState(false);

  // Use the story ideas hook for single show mode
  const {
    loading: ideasLoading,
    ideas,
    error: ideasError,
    generateIdeas: generateSingleShowIdeas,
    clearError: clearIdeasError,
    clearIdeas,
  } = useStoryIdeas();

  // Blast mode state
  const [currentShowName, setCurrentShowName] = useState('');
  const [totalShows, setTotalShows] = useState(0);
  const [processedShows, setProcessedShows] = useState(0);
  const [blastStoryTitles, setBlastStoryTitles] = useState<string[]>([]);
  const [lastCompletedShowName, setLastCompletedShowName] = useState('');

  const [create] = useCreate();
  const redirect = useRedirect();
  const notify = useNotify();

  // Get list of shows for the dropdown
  // Sort by slug since name column moved to show_translations
  const { data: shows } = useGetList('shows', {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'slug', order: 'ASC' },
    filter: {},
  });

  const enabledShows = (shows ?? []).filter(show => show.enabled !== false);

  // Use shared unsaved changes hook
  const { handleCancel: handleUnsavedCancel } = useUnsavedChanges({
    dependencies: [
      manualTopic,
      manualTitle,
      manualDescription,
      manualBrief,
      manualTakeaway,
      manualKeywords,
      manualShowSlug,
      generateShowSlug,
      storyIdeaCount,
    ],
    onCancel: () => redirect('list', 'stories'),
  });

  useEffect(() => {
    if (generateMode !== 'blast') {
      setBlastStoryTitles([]);
      setCurrentShowName('');
      setLastCompletedShowName('');
      setTotalShows(0);
      setProcessedShows(0);
    }
  }, [generateMode]);

  // Update selected ideas when ideas from hook changes
  useEffect(() => {
    if (ideas.length > 0) {
      setSelectedIdeas(new Array(ideas.length).fill(true));
    }
  }, [ideas]);

  const generateStory = async () => {
    if (
      !manualTopic?.trim() ||
      !manualTitle?.trim() ||
      !manualSlug?.trim() ||
      !manualDescription?.trim() ||
      !manualBrief?.trim() ||
      !manualTakeaway?.trim() ||
      !manualShowSlug?.trim() ||
      isGenerating
    ) {
      return;
    }
    setIsGenerating(true);

    try {
      // Get show data for validation
      const selectedShow = shows?.find(show => show.slug === manualShowSlug);
      if (!selectedShow) {
        throw new Error('Selected show not found');
      }

      // Sluggify the provided slug and add 5-digit random suffix
      const baseSlug = slugify(manualSlug.trim());
      const randomSuffix = Math.floor(10000 + Math.random() * 90000); // 5-digit random number
      const slug = `${baseSlug}-${randomSuffix}`;

      // Create the story directly in the database with all seed fields
      const storyData = {
        topic: manualTopic.trim(),
        title: manualTitle.trim(),
        description: manualDescription.trim(),
        brief: manualBrief.trim(),
        takeaway: manualTakeaway.trim(),
        searchTerms: manualKeywords.trim(),
        showSlug: manualShowSlug,
        slug,
      };

      await create('stories', { data: storyData });
      notify(
        'Story created successfully! Script will be generated automatically.'
      );

      // Navigate back to stories list
      redirect('list', 'stories');
    } catch (error) {
      notify(
        error instanceof Error
          ? error.message
          : 'Failed to create story. Please try again.',
        { type: 'error' }
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const generateIdeas = async () => {
    if (
      ideasLoading ||
      (generateMode === 'specific' && !generateShowSlug?.trim())
    )
      return;

    logger.debug('🚀 Starting idea generation:', {
      generateMode,
      generateShowSlug,
      storyIdeaCount,
    });

    const validCount =
      typeof storyIdeaCount === 'number' &&
      !Number.isNaN(storyIdeaCount) &&
      storyIdeaCount >= 1 &&
      storyIdeaCount <= 20
        ? storyIdeaCount
        : 1;

    if (generateMode === 'blast') {
      if (!shows || enabledShows.length === 0) {
        notify('No shows available for blast generation', { type: 'error' });
        return;
      }

      // Reset idea selection state before blast run
      clearIdeas();
      setSelectedIdeas([]);

      // Start blast mode processing - client iterates through shows
      const summary = await handleBlastModeStoryCreation(validCount);
      logger.debug('🚀 Blast mode summary:', summary);
      if (summary.successfulShows === 0) {
        const failureMessage =
          summary.failedShows.length > 0
            ? `Blast mode failed for ${summary.failedShows.join(', ')}`
            : 'Blast mode failed with no stories created.';
        notify(failureMessage, { type: 'error' });
        return;
      }

      const successMessage = `Blast completed! Created ${summary.totalStoriesCreated} stories across ${summary.successfulShows} shows.`;
      notify(successMessage, { type: 'success' });

      if (summary.failedShows.length > 0) {
        notify(
          `Skipped ${summary.failedShows.length} show(s): ${summary.failedShows.join(', ')}`,
          { type: 'warning' }
        );
      }

      // Navigate back to stories list after successful blast
      redirect('list', 'stories');
    } else {
      // For specific show, use the hook which handles queue polling
      const selectedShow = shows?.find(show => show.slug === generateShowSlug);
      if (!selectedShow) {
        notify('Selected show not found', { type: 'error' });
        return;
      }

      logger.debug('📤 Creating queue job for story ideas:', {
        showSlug: generateShowSlug,
        storyIdeaCount: validCount,
      });

      // Use the hook - it will poll for completion and update ideas state
      await generateSingleShowIdeas({
        showSlug: generateShowSlug,
        storyIdeaCount: validCount,
        shouldSave: false, // Preview mode - don't save to DB
      });
    }
  };

  const handleBlastModeStoryCreation = async (
    validCount: number
  ): Promise<{
    totalStoriesCreated: number;
    successfulShows: number;
    failedShows: string[];
  }> => {
    if (!shows || shows.length === 0) {
      return { totalStoriesCreated: 0, successfulShows: 0, failedShows: [] };
    }

    const blastShows = enabledShows;
    setTotalShows(blastShows.length);
    setProcessedShows(0);
    setCurrentShowName('');
    setLastCompletedShowName('');
    setBlastStoryTitles([]);

    let totalStoriesCreated = 0;
    let successfulShows = 0;
    const failedShows: string[] = [];

    const endpoint = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/gen-story-ideas`;

    for (let index = 0; index < blastShows.length; index += 1) {
      const show = blastShows[index];
      const showName = show.name || show.title || show.slug;

      setCurrentShowName(showName);

      try {
        // Step 1: Create queue job
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: await getEdgeFunctionAuthHeaders(),
          body: JSON.stringify({
            showSlug: show.slug,
            storyIdeaCount: validCount,
            shouldSave: true, // Blast mode - save stories to DB
            trigger: 'manual', // UI-initiated jobs get priority
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          const errorMessage =
            (typeof data?.error === 'string' && data.error) || 'Request failed';
          throw new Error(errorMessage);
        }

        if (!data.success || !data.result?.jobId) {
          throw new Error(data.error || 'Failed to create queue job');
        }

        const { jobId } = data.result;
        logger.debug('📋 Blast mode: job created', {
          showSlug: show.slug,
          jobId,
        });

        // Step 2: Wait for job completion (blast mode waits for each job
        // sequentially; shared polling utility, issue #739)
        const jobResult = await waitForJob(jobId, {
          timeoutMs: 3 * 60 * 1000, // 3 minutes
          pollIntervalMs: 2000, // 2 seconds
        });

        if (jobResult.outcome !== 'completed') {
          throw new Error(jobResult.error || 'Job failed');
        }

        // Step 3: Extract stories from output
        const stories =
          (jobResult.job.output?.stories as Array<{
            title: string;
            brief: string;
          }>) || [];

        if (stories.length === 0) {
          failedShows.push(showName);
          continue;
        }

        const titles = stories.map((story, storyIndex) => {
          const trimmedTitle = story.title?.trim();
          if (trimmedTitle) {
            return trimmedTitle;
          }
          const fallbackBrief = story.brief?.trim();
          if (fallbackBrief) {
            return fallbackBrief.slice(0, 80);
          }
          return `Story ${storyIndex + 1}`;
        });

        setBlastStoryTitles(titles);
        setLastCompletedShowName(showName);

        totalStoriesCreated += stories.length;
        successfulShows += 1;
      } catch (error) {
        logger.error('❌ Blast mode generation failed for show', {
          showSlug: show.slug,
          error,
        });
        failedShows.push(showName);
      } finally {
        setProcessedShows(index + 1);
      }
    }

    setCurrentShowName('');

    return { totalStoriesCreated, successfulShows, failedShows };
  };

  // Handle copying an idea to the manual form
  // Note: AI returns topic, brief, slug, takeaway, taxonomy, imagePrompt
  // title, description, keywords are filled in by user or generated later
  const handleCopyIdea = (idea: {
    topic: string;
    brief: string;
    takeaway?: string;
  }) => {
    setManualTopic(idea.topic);
    setManualTitle(''); // Title is in story_translations, generated later
    setManualDescription(''); // Description is in story_translations, generated later
    setManualBrief(idea.brief);
    setManualTakeaway(idea.takeaway || '');
    setManualKeywords(''); // Not returned by AI anymore

    // Also apply the selected show from the Generate Ideas section to the Manual section
    if (generateShowSlug && generateMode === 'specific') {
      setManualShowSlug(generateShowSlug);
      notify(
        'Idea and show copied to manual form. Click "Create Story" to proceed.'
      );
    } else {
      notify(
        'Idea copied to manual form. Please select a show and click "Create Story" to proceed.'
      );
    }
  };

  const handleIdeaToggle = (index: number) => {
    const newSelected = [...selectedIdeas];
    newSelected[index] = !newSelected[index];
    setSelectedIdeas(newSelected);
  };

  const handleSelectAll = () => {
    const allSelected = selectedIdeas.every(Boolean);
    setSelectedIdeas(new Array(ideas.length).fill(!allSelected));
  };

  const isAllSelected =
    selectedIdeas.length > 0 && selectedIdeas.every(Boolean);
  const isSomeSelected = selectedIdeas.some(Boolean);
  const isIndeterminate = isSomeSelected && !isAllSelected;

  const handleGenerateSelectedStories = async () => {
    const selectedStoryIdeas = ideas.filter((_, index) => selectedIdeas[index]);

    if (selectedStoryIdeas.length === 0) {
      notify('Please select at least one story idea', { type: 'error' });
      return;
    }

    // For blast mode, skip - stories are already created with shouldSave: true
    if (generateMode === 'blast') {
      notify(
        'Blast mode creates stories directly. Use a specific show to preview ideas first.',
        { type: 'warning' }
      );
      return;
    }

    const selectedShow = shows?.find(show => show.slug === generateShowSlug);
    if (!selectedShow) {
      notify('Please select a show first', { type: 'error' });
      return;
    }

    setIsGenerating(true);
    let successCount = 0;

    try {
      // Create stories in the database for each selected idea
      for (const idea of selectedStoryIdeas) {
        // Validate required fields (topic, brief, slug come from AI)
        if (!idea.topic || !idea.brief || !idea.slug) {
          throw new Error(
            'Story idea missing required field (topic, brief, or slug)'
          );
        }

        // Add random suffix to ensure slug uniqueness
        const randomSuffix = Math.floor(10000 + Math.random() * 90000);
        const slug = `${idea.slug}-${randomSuffix}`;

        // Only send fields that belong in stories table
        // title, subtitle, description are in story_translations (generated later)
        const storyData = {
          topic: idea.topic,
          brief: idea.brief,
          takeaway: idea.takeaway || '',
          taxonomy: idea.taxonomy || '',
          imagePrompt: idea.imagePrompt || '',
          showSlug: generateShowSlug,
          slug,
        };

        await create('stories', { data: storyData });
        successCount += 1;
      }

      if (successCount > 0) {
        notify(
          `${successCount} story brief${successCount > 1 ? 's' : ''} created successfully! Scripts will be generated automatically.`
        );
        // Navigate back to stories list
        redirect('list', 'stories');
      } else {
        notify('No stories were created. Please try again.', { type: 'error' });
      }
    } catch (error) {
      notify(
        error instanceof Error
          ? error.message
          : 'Failed to create stories. Please try again.',
        { type: 'error' }
      );
    } finally {
      setIsGenerating(false);
    }
  };

  // clearIdeasError is provided by useStoryIdeas hook

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '100%',
        maxWidth: LAYOUT.narrowContentWidth,
        mx: 'auto',
        pt: 2,
        px: 2,
      }}
    >
      {/* Header with back button */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={handleUnsavedCancel} sx={{ p: 1 }}>
          <ArrowLeft />
        </IconButton>
        <Typography variant="h5" component="h1">
          Add Stories
        </Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Generate" />
          <Tab label="Manual" />
        </Tabs>
      </Box>

      {/* Tab 1: Generate Ideas */}
      {activeTab === 0 && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            <FormControl>
              <RadioGroup
                row
                value={generateMode}
                onChange={e =>
                  setGenerateMode(e.target.value as 'specific' | 'blast')
                }
              >
                <FormControlLabel
                  value="specific"
                  control={<Radio />}
                  label="Specific show"
                />
                <FormControlLabel
                  value="blast"
                  control={<Radio />}
                  label="🚀 Blast: All shows (no preview)"
                />
              </RadioGroup>
            </FormControl>

            {generateMode === 'specific' && (
              <SearchSelect
                label="Show"
                placeholder="Search shows..."
                required
                options={(shows ?? []).map(show => ({
                  id: show.id,
                  label: show.name || show.slug,
                  slug: show.slug,
                }))}
                value={generateShowSlug}
                onChange={value => setGenerateShowSlug(value ?? '')}
                width="medium"
              />
            )}

            <FormTextField
              label="Count"
              type="number"
              value={storyIdeaCount}
              onChange={e => {
                const { value } = e.target;
                // Handle empty string during editing
                if (value === '') {
                  setStoryIdeaCount(1); // Use 1 as default when field is empty
                  return;
                }
                const parsed = parseInt(value, 10);
                if (Number.isNaN(parsed)) {
                  setStoryIdeaCount(1); // Use 1 as fallback for invalid input
                  return;
                }
                setStoryIdeaCount(Math.max(1, Math.min(20, parsed)));
              }}
              width="small"
              size="small"
              inputProps={{ min: 1, max: 20 }}
              sx={{ width: 120 }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Button
                variant="outlined"
                onClick={generateIdeas}
                disabled={
                  ideasLoading ||
                  isGenerating ||
                  (generateMode === 'specific' && !generateShowSlug?.trim())
                }
                startIcon={ideasLoading ? <CircularProgress size={20} /> : null}
              >
                {ideasLoading
                  ? generateMode === 'blast'
                    ? 'Creating stories for all shows...'
                    : 'Generating Ideas...'
                  : generateMode === 'blast' && enabledShows.length > 0
                    ? `Create ${storyIdeaCount * enabledShows.length} stories`
                    : `Generate ${storyIdeaCount} ideas`}
              </Button>
            </Box>

            {generateMode === 'blast' && (
              <>
                {ideasLoading && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      bgcolor: 'action.hover',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2" color="primary" gutterBottom>
                      {totalShows > 0
                        ? `Creating stories: ${processedShows}/${totalShows} shows complete`
                        : 'Preparing blast generation...'}
                    </Typography>
                    {currentShowName && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        {`Processing ${currentShowName}`}
                      </Typography>
                    )}
                    {lastCompletedShowName && blastStoryTitles.length > 0 && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        {`Latest: ${lastCompletedShowName}`}
                      </Typography>
                    )}
                  </Box>
                )}

                {blastStoryTitles.length > 0 && (
                  <Card variant="outlined" sx={{ mt: 2 }}>
                    <CardContent>
                      {lastCompletedShowName && (
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          {`Generated for ${lastCompletedShowName}`}
                        </Typography>
                      )}
                      <List dense>
                        {blastStoryTitles.map((title, index) => (
                          <ListItem
                            key={`blast-title-${index}`}
                            sx={{ py: 0.5 }}
                          >
                            <Typography variant="body2">{title}</Typography>
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </Box>

          {ideasError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={clearIdeasError}>
              {ideasError}
            </Alert>
          )}

          {ideas.length > 0 && (
            <Card variant="outlined">
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle1">Generated Ideas</Typography>
                  <Button
                    variant="contained"
                    onClick={handleGenerateSelectedStories}
                    disabled={
                      isGenerating || selectedIdeas.filter(Boolean).length === 0
                    }
                    startIcon={
                      isGenerating ? <CircularProgress size={20} /> : null
                    }
                  >
                    {isGenerating
                      ? 'Creating Stories...'
                      : `Create ${selectedIdeas.filter(Boolean).length} stories`}
                  </Button>
                </Box>

                {/* Select All/Deselect All Checkbox and Show Details Switch */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                    px: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox
                      checked={isAllSelected}
                      indeterminate={isIndeterminate}
                      onChange={handleSelectAll}
                      disabled={isGenerating}
                      size="small"
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ ml: 1 }}
                    >
                      {isAllSelected ? 'Deselect all' : 'Select all'}
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showDetails}
                        onChange={e => setShowDetails(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Show details"
                    sx={{ mr: 0 }}
                  />
                </Box>

                <List>
                  {ideas.map((idea, index) => (
                    <ListItem
                      key={`story-${index}-${idea.topic.slice(0, 20).replace(/[^a-zA-Z0-9]/g, '')}`}
                      disablePadding
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          width: '100%',
                          p: 1,
                        }}
                      >
                        <Checkbox
                          checked={selectedIdeas[index] || false}
                          onChange={() => handleIdeaToggle(index)}
                          disabled={isGenerating}
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                        <Box sx={{ flex: 1, ml: 1 }}>
                          {idea.topic && (
                            <Typography
                              variant="body1"
                              fontWeight="bold"
                              sx={{ lineHeight: 1.4 }}
                            >
                              {idea.topic}
                            </Typography>
                          )}
                          {idea.brief && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ lineHeight: 1.4, mt: 0.5 }}
                            >
                              {idea.brief}
                            </Typography>
                          )}
                          {idea.takeaway && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                lineHeight: 1.4,
                                mt: 0.5,
                                fontStyle: 'italic',
                              }}
                            >
                              ☝️ {idea.takeaway}
                            </Typography>
                          )}
                          {/* Show additional details when switch is on */}
                          {showDetails && idea.taxonomy && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ lineHeight: 1.4, mt: 0.5 }}
                            >
                              <strong>Taxonomy:</strong> {idea.taxonomy}
                            </Typography>
                          )}
                          {showDetails && idea.imagePrompt && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ lineHeight: 1.4, mt: 0.5 }}
                            >
                              <strong>Image Prompt:</strong> {idea.imagePrompt}
                            </Typography>
                          )}
                        </Box>
                        <IconButton
                          onClick={() => handleCopyIdea(idea)}
                          size="small"
                          disabled={isGenerating}
                          sx={{
                            ml: 1,
                            color: 'action.secondary',
                            '&:hover': {
                              backgroundColor: 'action.focus',
                              color: 'primary.main',
                            },
                          }}
                        >
                          <Copy size={16} />
                        </IconButton>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {/* Tab 2: Manual Story Creation */}
      {activeTab === 1 && (
        <Box>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Create a story brief. Scripts will be generated automatically by the
            system.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl sx={{ width: 'fit-content', minWidth: 300 }}>
              <InputLabel>Show *</InputLabel>
              <Select
                value={manualShowSlug}
                onChange={e => setManualShowSlug(e.target.value)}
                label="Show *"
                required
              >
                {shows?.map(show => (
                  <MenuItem key={show.id} value={show.slug}>
                    {show.name || show.slug}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormTextField
              label="Topic"
              value={manualTopic}
              onChange={e => setManualTopic(e.target.value)}
              placeholder="What topic area does this story cover?"
              required
            />

            <FormTextField
              label="Title"
              value={manualTitle}
              onChange={e => setManualTitle(e.target.value)}
              placeholder="Story title"
              required
            />

            <FormTextField
              label="Slug"
              value={manualSlug}
              onChange={e => setManualSlug(e.target.value)}
              placeholder="URL-friendly slug (e.g., story-about-science)"
              required
            />

            <FormTextField
              label="Description"
              value={manualDescription}
              onChange={e => setManualDescription(e.target.value)}
              placeholder="Brief description of the story"
              required
              rows={2}
            />

            <FormTextField
              label="Keywords"
              value={manualKeywords}
              onChange={e => setManualKeywords(e.target.value)}
              placeholder="Comma-separated keywords for search (optional)"
            />

            <FormTextField
              label="Brief"
              value={manualBrief}
              onChange={e => setManualBrief(e.target.value)}
              placeholder="Detailed brief about what the story should cover..."
              required
              rows={3}
            />

            <FormTextField
              label="Takeaway"
              value={manualTakeaway}
              onChange={e => setManualTakeaway(e.target.value)}
              placeholder="What should the listener learn or take away from this story?"
              required
              rows={2}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Button
                variant="contained"
                onClick={() => generateStory()}
                disabled={
                  !manualTopic.trim() ||
                  !manualTitle.trim() ||
                  !manualSlug.trim() ||
                  !manualDescription.trim() ||
                  !manualBrief.trim() ||
                  !manualTakeaway.trim() ||
                  !manualShowSlug.trim() ||
                  isGenerating ||
                  ideasLoading
                }
                startIcon={isGenerating ? <CircularProgress size={20} /> : null}
              >
                {isGenerating ? 'Creating...' : 'Create Story'}
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

// Export as StoryCreate for backwards compatibility
export const StoryCreate = StoryCreateWrapper;
